import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import api from "../lib/api";
import { DEFAULT_CONTENT, mergeContent, BOARD, SECTION_META } from "../lib/content";
import { ICONS } from "../lib/icons";
import ImageUploader from "../components/ImageUploader";

const STAT_SOURCES = { manual: "رقم يدوي", members: "تلقائي: أعضاء النادي", committees: "تلقائي: عدد اللجان", plays: "تلقائي: المسرحيات", workshops: "تلقائي: الفعاليات والدورات", achievements: "تلقائي: الإنجازات" };
const inputCls = "w-full input-theater rounded-lg px-3 py-2 text-sm";
const labelCls = "text-xs text-[#BFB3A0] mb-1 block";

function Text({ label, value, onChange, area, tid }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {area
        ? <textarea data-testid={tid} className={`${inputCls} min-h-[80px]`} value={value || ""} onChange={(e) => onChange(e.target.value)} />
        : <input data-testid={tid} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}

function ListEditor({ label, items, onChange, tid, render }) {
  const move = (i, d) => { const a = [...items]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className={labelCls}>{label}</label>
        <button type="button" data-testid={`${tid}-add`} onClick={() => onChange([...items, render.empty])} className="btn-ghost rounded-full px-3 py-1 text-xs inline-flex items-center gap-1"><Plus size={12} /> إضافة</button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} data-testid={`${tid}-item-${i}`} className="theater-card rounded-xl p-3 flex gap-2 items-start">
            <div className="flex flex-col gap-1 pt-1">
              <button type="button" onClick={() => move(i, -1)} className="text-[#BFB3A0] hover:text-[#F0E8DB]"><ChevronUp size={14} /></button>
              <button type="button" onClick={() => move(i, 1)} className="text-[#BFB3A0] hover:text-[#F0E8DB]"><ChevronDown size={14} /></button>
            </div>
            <div className="flex-1 space-y-2">{render.fields(it, (v) => onChange(items.map((x, j) => (j === i ? v : x))), i)}</div>
            <button type="button" data-testid={`${tid}-remove-${i}`} onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-[#943833] hover:text-red-400 pt-1"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="theater-card rounded-2xl p-6 space-y-4">
      <h3 className="text-[#F0E8DB] font-semibold text-lg border-b border-[#F0E8DB]/15 pb-3">{title}</h3>
      {children}
    </div>
  );
}

export default function ContentPanel({ onSaved }) {
  const [c, setC] = useState(null);
  const [orig, setOrig] = useState({});
  const [saving, setSaving] = useState(false);
  const withIds = (m) => ({ ...m, committee_list: m.committee_list.map((x) => ({ ...x, _id: x._id || Math.random().toString(36).slice(2) })) });
  useEffect(() => { api.get("/content").then((r) => { const m = withIds(mergeContent(r.data)); setC(m); setOrig(Object.fromEntries(m.committee_list.map((x) => [x._id, x.name]))); }); }, []);
  if (!c) return null;
  const set = (k, v) => setC({ ...c, [k]: v });
  const setTitle = (k, v) => setC({ ...c, titles: { ...c.titles, [k]: v } });
  const setCom = (name, k, v) => setC({ ...c, committees: { ...c.committees, [name]: { ...c.committees[name], [k]: v } } });
  const COMMITTEE_KEYS = [BOARD, ...c.committee_list.map((x) => x.name)];
  const toggleHidden = (k) => set("hidden_sections", c.hidden_sections.includes(k) ? c.hidden_sections.filter((x) => x !== k) : [...c.hidden_sections, k]);
  const moveSection = (i, d) => { const a = [...c.section_order]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; set("section_order", a); };
  const renameCommittee = (i, newName) => {
    const oldName = c.committee_list[i].name;
    const list = c.committee_list.map((x, j) => (j === i ? { ...x, name: newName } : x));
    const committees = { ...c.committees };
    if (oldName !== newName) { committees[newName] = committees[oldName] || {}; delete committees[oldName]; }
    setC({ ...c, committee_list: list, committees });
  };
  const save = async () => {
    setSaving(true);
    try {
      const kept = c.committee_list.filter((x) => x.name.trim()).map((x) => ({ ...x, _id: x._id || Math.random().toString(36).slice(2) }));
      const payload = { ...c, committee_list: kept.map(({ _id, ...x }) => x) };
      const r = await api.put("/content", payload);
      const renames = kept.map((x) => [orig[x._id], x.name]).filter(([o, n]) => o && n && o !== n);
      if (renames.length) {
        const [team, mom] = await Promise.all([api.get("/team"), api.get("/members-of-month")]);
        const jobs = [];
        renames.forEach(([o, n]) => {
          team.data.filter((m) => m.committee === o).forEach((m) => { const { id, created_at, updated_at, ...b } = m; jobs.push(api.put(`/team/${id}`, { ...b, committee: n })); });
          mom.data.filter((m) => m.committee === o).forEach((m) => { const { id, created_at, updated_at, ...b } = m; jobs.push(api.put(`/members-of-month/${id}`, { ...b, committee: n })); });
        });
        await Promise.all(jobs);
      }
      setOrig(Object.fromEntries(kept.map((x) => [x._id, x.name])));
      setC({ ...c, committee_list: kept });
      onSaved?.(r.data);
      toast.success("تم حفظ المحتوى");
    } catch { toast.error("فشل الحفظ"); }
    finally { setSaving(false); }
  };
  const reset = () => { if (window.confirm("استرجاع النصوص الأصلية؟ (لن يُحفظ حتى تضغط حفظ)")) setC(withIds(mergeContent(null))); };

  return (
    <div data-testid="content-panel" className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between sticky top-0 z-10 bg-[#120908]/95 backdrop-blur py-3 -mt-3">
        <h2 className="text-2xl font-semibold text-[#F0E8DB]">محتوى الموقع</h2>
        <div className="flex gap-2">
          <button data-testid="content-reset" onClick={reset} className="btn-ghost rounded-full px-4 py-2 text-sm">استرجاع الأصل</button>
          <button data-testid="content-save" onClick={save} disabled={saving} className="btn-gold rounded-full px-5 py-2 text-sm inline-flex items-center gap-1"><Save size={15} /> {saving ? "..." : "حفظ"}</button>
        </div>
      </div>

      <Section title="الأقسام: الترتيب والإظهار">
        <p className="text-[#A89A88] text-xs">حرّك الأقسام لتغيير ترتيبها في الصفحة، أو أخفِ أي قسم مؤقتاً (يختفي من الصفحة والقائمة العلوية).</p>
        <div className="space-y-2">
          {c.section_order.map((k, i) => {
            const hidden = c.hidden_sections.includes(k);
            return (
              <div key={k} data-testid={`section-row-${k}`} className={`theater-card rounded-xl px-3 py-2 flex items-center gap-3 ${hidden ? "opacity-50" : ""}`}>
                <div className="flex flex-col gap-0.5">
                  <button type="button" data-testid={`section-up-${k}`} onClick={() => moveSection(i, -1)} className="text-[#BFB3A0] hover:text-[#F0E8DB]"><ChevronUp size={14} /></button>
                  <button type="button" data-testid={`section-down-${k}`} onClick={() => moveSection(i, 1)} className="text-[#BFB3A0] hover:text-[#F0E8DB]"><ChevronDown size={14} /></button>
                </div>
                <div className="flex-1 text-sm text-[#F0E8DB]">{SECTION_META[k]}</div>
                {c.titles[k] !== undefined && (
                  <input data-testid={`title-${k}`} className={`${inputCls} max-w-xs`} value={c.titles[k]} onChange={(e) => setTitle(k, e.target.value)} placeholder="عنوان القسم" />
                )}
                <button type="button" data-testid={`section-toggle-${k}`} onClick={() => toggleHidden(k)} className="btn-ghost rounded-full px-3 py-1.5 text-xs inline-flex items-center gap-1">
                  {hidden ? <><EyeOff size={13} /> مخفي</> : <><Eye size={13} /> ظاهر</>}
                </button>
              </div>
            );
          })}
        </div>
        <label className="flex items-center gap-3 text-sm text-[#F0E8DB] cursor-pointer">
          <input type="checkbox" data-testid="content-easter-egg" checked={c.easter_egg !== false} onChange={(e) => set("easter_egg", e.target.checked)} className="accent-[#7A2E2A] w-4 h-4" />
          تفعيل المفاجأة المسرحية (لعبة "وش دورك على الخشبة؟" تظهر عند الوصول لنهاية الصفحة أو بالضغط على اللوقو 5 مرات)
        </label>
        <Text label="عنوان بطاقة مجلس الإدارة" value={c.titles.board} onChange={(v) => setTitle("board", v)} tid="title-board" />
      </Section>

      <Section title="شريط العدادات">
        <p className="text-[#A89A88] text-xs">اختر لكل عدّاد مصدره: تلقائي (يُحسب من بيانات الموقع) أو رقم يدوي تكتبه أنت. عدد الأعضاء التلقائي يستخدم "عدد أعضاء النادي" من الإعدادات إن وُجد، وإلا عدد أعضاء الفريق.</p>
        <ListEditor label="العدادات (بالترتيب)" items={c.stats} onChange={(v) => set("stats", v)} tid="content-stats"
          render={{ empty: { label: "", icon: "star", source: "manual", value: 0 }, fields: (it, upd) => (
            <div className="grid sm:grid-cols-4 gap-2">
              <input className={inputCls} placeholder="التسمية (مثال: عضو في النادي)" value={it.label} onChange={(e) => upd({ ...it, label: e.target.value })} />
              <select className={inputCls} value={it.source || "manual"} onChange={(e) => upd({ ...it, source: e.target.value })}>
                {Object.entries(STAT_SOURCES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input type="number" className={inputCls} placeholder="الرقم" disabled={it.source && it.source !== "manual"} value={it.value ?? 0} onChange={(e) => upd({ ...it, value: e.target.value })} />
              <select className={inputCls} value={it.icon} onChange={(e) => upd({ ...it, icon: e.target.value })}>
                {Object.entries(ICONS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          ) }} />
      </Section>

      <Section title="اللجان: الأسماء والأيقونات والصور">
        <p className="text-[#A89A88] text-xs">تعديل اسم لجنة هنا يُحدّث تلقائياً أعضاءها وأعضاء الشهر المرتبطين بها عند الحفظ. الحذف يُخفي بطاقة اللجنة فقط.</p>
        <ListEditor label="اللجان (بالترتيب)" items={c.committee_list} onChange={(v) => set("committee_list", v)} tid="content-committees"
          render={{ empty: { name: "", icon: "star", image: "" }, fields: (it, upd, i) => (<>
            <div className="grid sm:grid-cols-2 gap-2">
              <input className={inputCls} placeholder="اسم اللجنة" value={it.name} onChange={(e) => renameCommittee(i, e.target.value)} />
              <select className={inputCls} value={it.icon} onChange={(e) => upd({ ...it, icon: e.target.value })}>
                {Object.entries(ICONS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <ImageUploader value={it.image} onChange={(v) => upd({ ...it, image: v })} testid={`committee-image-${i}`} />
          </>) }} />
      </Section>

      <Section title="الصفحة الرئيسية — المقولة">
        <Text label="المقولة" value={c.hero_quote} onChange={(v) => set("hero_quote", v)} area tid="content-hero_quote" />
        <Text label="القائل" value={c.hero_quote_author} onChange={(v) => set("hero_quote_author", v)} tid="content-hero_quote_author" />
      </Section>

      <Section title="لماذا تختارنا">
        <Text label="العبارة التعريفية" value={c.why_us_intro} onChange={(v) => set("why_us_intro", v)} area tid="content-why_us_intro" />
        <ListEditor label="الأدوار المتاحة (شريط الأدوار)" items={c.why_us_roles} onChange={(v) => set("why_us_roles", v)} tid="content-roles"
          render={{ empty: "", fields: (it, upd) => <input className={inputCls} value={it} onChange={(e) => upd(e.target.value)} /> }} />
        <ListEditor label="النقاط" items={c.why_us} onChange={(v) => set("why_us", v)} tid="content-whyus"
          render={{ empty: { title: "", text: "" }, fields: (it, upd) => (<>
            <input className={inputCls} placeholder="العنوان" value={it.title} onChange={(e) => upd({ ...it, title: e.target.value })} />
            <textarea className={`${inputCls} min-h-[60px]`} placeholder="النص" value={it.text} onChange={(e) => upd({ ...it, text: e.target.value })} />
          </>) }} />
      </Section>

      <Section title="أهداف النادي">
        <Text label="العبارة التعريفية" value={c.goals_intro} onChange={(v) => set("goals_intro", v)} area tid="content-goals_intro" />
        <ListEditor label="الأهداف (بالترتيب)" items={c.goals} onChange={(v) => set("goals", v)} tid="content-goals"
          render={{ empty: "", fields: (it, upd) => <textarea className={`${inputCls} min-h-[60px]`} value={it} onChange={(e) => upd(e.target.value)} /> }} />
      </Section>

      <Section title="اللجان — التعريفات">
        <Text label="العبارة التعريفية للقسم" value={c.team_intro} onChange={(v) => set("team_intro", v)} area tid="content-team_intro" />
        {COMMITTEE_KEYS.map((name) => (
          <div key={name} data-testid={`content-committee-${name}`} className="rounded-xl border border-[#F0E8DB]/15 p-4 space-y-3">
            <div className="text-[#F0E8DB] font-semibold">{name}</div>
            <Text label="الشعار القصير" value={c.committees[name]?.tagline} onChange={(v) => setCom(name, "tagline", v)} />
            <Text label="ماذا نفعل" value={c.committees[name]?.does} onChange={(v) => setCom(name, "does", v)} area />
            <Text label="طموحنا" value={c.committees[name]?.ambition} onChange={(v) => setCom(name, "ambition", v)} area />
            <Text label="ماذا تستفيد كطالب" value={c.committees[name]?.gain} onChange={(v) => setCom(name, "gain", v)} area />
          </div>
        ))}
      </Section>

      <Section title="أعضاء الشهر وشركاء النجاح">
        <Text label="عبارة قسم أعضاء الشهر" value={c.mom_intro} onChange={(v) => set("mom_intro", v)} area tid="content-mom_intro" />
        <Text label="عبارة قسم شركاء النجاح" value={c.partners_intro} onChange={(v) => set("partners_intro", v)} tid="content-partners_intro" />
        <Text label="عبارة شريط الصور" value={c.gallery_intro} onChange={(v) => set("gallery_intro", v)} tid="content-gallery_intro" />
        <Text label="نص يظهر عند عدم وجود شركاء" value={c.partners_empty} onChange={(v) => set("partners_empty", v)} tid="content-partners_empty" />
      </Section>

      <div className="flex justify-end">
        <button data-testid="content-save-bottom" onClick={save} disabled={saving} className="btn-gold rounded-full px-6 py-2.5 text-sm inline-flex items-center gap-1"><Save size={15} /> حفظ كل التغييرات</button>
      </div>
    </div>
  );
}
